// ============================================================
// FGF-Geostrategy Insights Theme — Light main + Dark sidebar
// ============================================================

// Main content area (light)
export const colors = {
  bg: "#f8f9fb",
  bgCard: "#ffffff",
  bgHover: "#f0f1f4",
  bgActive: "#e8eaef",
  border: "#e2e4ea",
  borderLight: "#eff0f3",
  text: "#1a1d26",
  textSecondary: "#5a6178",
  accent: "#000f2d",
  accentHover: "#000925",
  accentDim: "rgba(0,15,45,0.08)",
  accentDimHover: "rgba(0,15,45,0.14)",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#6366f1",
  white: "#ffffff",
};

// Sidebar (dark navy — preserves FGF logo appearance)
export const sidebarColors = {
  bg: "#000f2d",
  bgHover: "#0a1a3d",
  bgActive: "#122148",
  border: "#1a2744",
  text: "#ffffff",
  textSecondary: "#c7c7cb",
  accent: "#c7c7cb",
  accentDim: "rgba(199,199,203,0.12)",
};

// Editor page (full-screen dark — renders outside AppShell)
export const editorColors = {
  bg: "#000925",
  bgCard: "#000f2d",
  bgHover: "#0a1a3d",
  bgActive: "#122148",
  border: "#1a2744",
  borderLight: "#2a3755",
  text: "#ffffff",
  textSecondary: "#c7c7cb",
  accent: "#c7c7cb",
  accentHover: "#ffffff",
  accentDim: "rgba(199,199,203,0.12)",
  accentDimHover: "rgba(199,199,203,0.20)",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#6366f1",
  white: "#ffffff",
};

export const fonts = {
  heading: "'Playfair Display', Georgia, serif",
  body: "'DM Sans', -apple-system, sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', monospace",
};

// Font size scale (px values)
export const fontSizes = {
  xxs: 9,
  xs: 10,
  sm: 11,
  base: 12,
  md: 13,
  lg: 14,
  xl: 16,
  "2xl": 18,
  "3xl": 20,
  "4xl": 26,
  "5xl": 28,
};

// Spacing scale (px values)
export const spacing = {
  0: 0,
  1: 2,
  2: 4,
  3: 6,
  4: 8,
  5: 10,
  6: 12,
  7: 16,
  8: 20,
  9: 24,
  10: 32,
  11: 40,
  12: 48,
};

// Border radii
export const radii = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
  full: 9999,
};

// Common reusable style objects
export const layout = {
  row: { display: "flex", alignItems: "center" },
  rowBetween: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  rowCenter: { display: "flex", alignItems: "center", justifyContent: "center" },
  col: { display: "flex", flexDirection: "column" },
  colCenter: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" },
  wrap: { display: "flex", flexWrap: "wrap" },
};

// Common text styles
export const text = {
  sectionLabel: {
    fontSize: fontSizes.sm,
    color: colors.textSecondary,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  heading: {
    fontFamily: fonts.heading,
    fontWeight: 700,
    color: colors.text,
  },
  body: {
    fontSize: fontSizes.md,
    color: colors.text,
  },
  secondary: {
    fontSize: fontSizes.base,
    color: colors.textSecondary,
  },
  mono: {
    fontFamily: fonts.mono,
    fontSize: fontSizes.base,
  },
  truncate: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

// Common surface/card styles
export const surfaces = {
  card: {
    background: colors.bgCard,
    borderRadius: radii.xl,
    border: `1px solid ${colors.border}`,
  },
  surface: {
    background: colors.bg,
    borderRadius: radii.xl,
    border: `1px solid ${colors.border}`,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    zIndex: 200,
  },
  modal: {
    position: "fixed",
    background: colors.bgCard,
    borderRadius: radii.xl,
    border: `1px solid ${colors.border}`,
    boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
    zIndex: 201,
  },
  successBanner: {
    padding: "8px 12px",
    borderRadius: radii.md,
    fontSize: fontSizes.base,
    background: "rgba(16,185,129,0.08)",
    color: colors.success,
  },
  errorBanner: {
    padding: "8px 12px",
    borderRadius: radii.md,
    fontSize: fontSizes.base,
    background: "rgba(239,68,68,0.08)",
    color: colors.danger,
  },
  warningBanner: {
    padding: "8px 12px",
    borderRadius: radii.md,
    fontSize: fontSizes.base,
    background: "rgba(245,158,11,0.08)",
    color: colors.warning,
  },
};

// Common interactive element styles
export const controls = {
  input: {
    width: "100%",
    padding: "8px 12px",
    borderRadius: radii.md,
    border: `1px solid ${colors.border}`,
    background: colors.bgCard,
    color: colors.text,
    fontFamily: fonts.body,
    fontSize: fontSizes.md,
    outline: "none",
  },
  iconButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    display: "flex",
    alignItems: "center",
  },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "3px 10px",
    borderRadius: radii.full,
    fontSize: fontSizes.sm,
    fontWeight: 600,
  },
};

export const CONTACT_TYPES = {
  client: {
    label: "Client",
    color: "#10b981",
    bg: "rgba(16,185,129,0.10)",
  },
  advisory_client: {
    label: "Advisory Client",
    color: "#059669",
    bg: "rgba(5,150,105,0.10)",
  },
  chairmans_access: {
    label: "Chairman's Access",
    color: "#047857",
    bg: "rgba(4,120,87,0.10)",
  },
  partner: { label: "Partner", color: "#8b5cf6", bg: "rgba(139,92,246,0.10)" },
  vip: { label: "VIP", color: "#d946ef", bg: "rgba(217,70,239,0.10)" },
  active_discussion: {
    label: "Active Discussion",
    color: "#f97316",
    bg: "rgba(249,115,22,0.10)",
  },
  prospect: {
    label: "Prospect",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.10)",
  },
  lead: { label: "Lead", color: "#eab308", bg: "rgba(234,179,8,0.10)" },
  media: { label: "Media", color: "#ec4899", bg: "rgba(236,72,153,0.10)" },
  former_client: {
    label: "Former Client",
    color: "#94a3b8",
    bg: "rgba(148,163,184,0.10)",
  },
  ffgf: { label: "FFGF", color: "#06b6d4", bg: "rgba(6,182,212,0.10)" },
  non_paying: {
    label: "Non-Paying",
    color: "#a78bfa",
    bg: "rgba(167,139,250,0.10)",
  },
  team: { label: "Team", color: "#3b82f6", bg: "rgba(59,130,246,0.10)" },
  inactive: {
    label: "Inactive",
    color: "#64748b",
    bg: "rgba(100,116,139,0.10)",
  },
  subscriber: {
    label: "Subscriber",
    color: "#6366f1",
    bg: "rgba(99,102,241,0.10)",
  },
};

/**
 * GHL tag categories. Tags not in either set are hidden from the UI.
 * Case-insensitive matching — all keys are lowercase.
 */
export const GHL_CONTENT_TAGS = new Set([
  "grs",
  "interest in ca",
  "masked newsletter",
  "monthly digest",
  "unsubscribed from newsletter",
  "webcast invite",
  "webcast registration",
  "weekly newsletter",
]);

export const GHL_CONTACT_TAGS = new Set([
  "academia np",
  "active discussion",
  "ffgf",
  "fgf",
  "fgf advisory client",
  "fgf chairman's access",
  "fgf chairman's access client",
  "former client",
  "gov/state np",
  "inactive",
  "lead",
  "partner/collaborator",
  "press",
  "prospect",
  "vip",
]);

export function classifyGhlTag(tag) {
  const lower = (tag || "").toLowerCase();
  if (GHL_CONTENT_TAGS.has(lower)) return "content";
  if (GHL_CONTACT_TAGS.has(lower)) return "contact";
  return null; // unknown — hide
}

export const EMAIL_FREQUENCIES = {
  all: { label: "All Emails", color: "#6366f1", bg: "rgba(99,102,241,0.10)" },
  digest: {
    label: "Monthly Digest",
    color: "#8b5cf6",
    bg: "rgba(139,92,246,0.10)",
  },
  none: { label: "No Emails", color: "#ef4444", bg: "rgba(239,68,68,0.10)" },
};

export const PIPELINE_STAGES = [
  { id: "lead", label: "Lead", color: "#94a3b8", defaultProbability: 10 },
  {
    id: "qualified",
    label: "Qualified",
    color: "#f59e0b",
    defaultProbability: 30,
  },
  {
    id: "proposal",
    label: "Proposal",
    color: "#6366f1",
    defaultProbability: 50,
  },
  {
    id: "negotiation",
    label: "Negotiation",
    color: "#3b82f6",
    defaultProbability: 70,
  },
  {
    id: "closed_won",
    label: "Closed Won",
    color: "#10b981",
    defaultProbability: 100,
  },
  {
    id: "closed_lost",
    label: "Closed Lost",
    color: "#ef4444",
    defaultProbability: 0,
  },
];

export const BOOK_SALE_STAGES = [
  { id: "lead", label: "Lead", color: "#94a3b8", defaultProbability: 10 },
  {
    id: "contacted",
    label: "Contacted",
    color: "#f59e0b",
    defaultProbability: 25,
  },
  {
    id: "proposal_sent",
    label: "Proposal Sent",
    color: "#8b5cf6",
    defaultProbability: 50,
  },
  { id: "won", label: "Won", color: "#10b981", defaultProbability: 100 },
  { id: "lost", label: "Lost", color: "#ef4444", defaultProbability: 0 },
];

export const ENHANCEMENT_STAGES = [
  { id: "suggested", label: "Suggested", color: "#94a3b8" },
  { id: "planned", label: "Planned", color: "#6366f1" },
  { id: "in_progress", label: "In Progress", color: "#f59e0b" },
  { id: "done", label: "Done", color: "#10b981" },
];

export const DOCUMENT_STATUSES = {
  draft: { label: "Draft", color: "#94a3b8", bg: "rgba(148,163,184,0.10)" },
  review: { label: "In Review", color: "#f59e0b", bg: "rgba(245,158,11,0.10)" },
  approved: {
    label: "Approved",
    color: "#3b82f6",
    bg: "rgba(59,130,246,0.10)",
  },
  sent: { label: "Sent", color: "#10b981", bg: "rgba(16,185,129,0.10)" },
  uploaded: {
    label: "Uploaded",
    color: "#6366f1",
    bg: "rgba(99,102,241,0.10)",
  },
  pending_signature: {
    label: "Pending Signature",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.10)",
  },
  signed: {
    label: "Signed",
    color: "#10b981",
    bg: "rgba(16,185,129,0.10)",
  },
};

export const SECTORS = [
  "Energy & Mining",
  "Infrastructure",
  "Defense & Security",
  "Finance & Capital Markets",
  "Technology",
  "Government & Public Sector",
  "Healthcare & Pharma",
  "Real Estate",
  "Agriculture & Food",
  "Media & Communications",
  "Other",
];

export const KANBAN_CADENCE_STAGES = [
  { id: "planning", label: "Planning", color: "#94a3b8" },
  { id: "scheduled", label: "Scheduled", color: "#f59e0b" },
  { id: "in-progress", label: "In Progress", color: "#6366f1" },
  { id: "complete", label: "Complete", color: "#10b981" },
  { id: "review", label: "Review", color: "#8b5cf6" },
];

export const KANBAN_ITEM_TYPES = {
  task: { label: "Task", color: "#3b82f6", bg: "rgba(59,130,246,0.1)" },
  engagement: { label: "Engagement", color: "#8b5cf6", bg: "rgba(139,92,246,0.1)" },
  event: { label: "Event", color: "#06b6d4", bg: "rgba(6,182,212,0.1)" },
};

export const KANBAN_PRIORITIES = {
  high: { label: "High", color: "#ef4444" },
  medium: { label: "Medium", color: "#f59e0b" },
  low: { label: "Low", color: "#94a3b8" },
};

export const MERGE_TAGS = [
  { tag: "{{first_name}}", label: "First Name", example: "Sarah" },
  { tag: "{{last_name}}", label: "Last Name", example: "Chen" },
  { tag: "{{company}}", label: "Company", example: "Meridian Capital" },
  { tag: "{{email}}", label: "Email", example: "s.chen@meridian.com" },
  { tag: "{{type}}", label: "Contact Type", example: "Client" },
  {
    tag: "{{unsubscribe_link}}",
    label: "Unsubscribe Link",
    example: "#unsubscribe",
  },
  {
    tag: "{{chairmans_access_link}}",
    label: "Chairman's Access Link",
    example: "#chairmans-access",
  },
  {
    tag: "{{webcast_register:",
    label: "Webcast Registration Link",
    example: "#register",
    needsEventId: true,
  },
];
